import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PERF_ENABLED, type PerfStat, perf } from './perf';

/**
 * Times this component's render through its layout effects (its children's
 * included): call at the top of the HUD's root. A no-op without `?perf=1`.
 */
export function usePerfCommit(): void {
  const start = PERF_ENABLED ? performance.now() : 0;
  useLayoutEffect(() => {
    if (PERF_ENABLED) perf.commit.add(performance.now() - start);
  });
}

/** Inside the Canvas: frame-to-frame time and the last frame's draw calls. */
export function PerfProbe() {
  const gl = useThree((s) => s.gl);
  const last = useRef<number | null>(null);
  useFrame(() => {
    const now = performance.now();
    if (last.current !== null) perf.frame.add(now - last.current);
    last.current = now;
    // Read before this frame renders: the counts are the previous frame's.
    perf.drawCalls = gl.info.render.calls;
  });
  return null;
}

interface Reading {
  sim: ReturnType<PerfStat['take']>;
  commit: ReturnType<PerfStat['take']>;
  frame: ReturnType<PerfStat['take']>;
  drawCalls: number;
  bytesOut: number;
  bytesIn: number;
}

const ms = (v: number) => v.toFixed(v < 10 ? 1 : 0);
const stat = (s: ReturnType<PerfStat['take']>) => (s ? `${ms(s.mean)} · ${ms(s.max)} max` : '—');
const rate = (bytes: number) =>
  bytes >= 1e6 ? `${(bytes / 1e6).toFixed(2)} MB/s` : `${(bytes / 1e3).toFixed(1)} KB/s`;

/** `?perf=1`: a small read-out of the probes, refreshed once a second. */
export function PerfOverlay({ isHost }: { isHost: boolean }) {
  const [reading, setReading] = useState<Reading | null>(null);
  useEffect(() => {
    if (!PERF_ENABLED) return;
    let last = performance.now();
    const timer = setInterval(() => {
      const now = performance.now();
      const secs = Math.max((now - last) / 1000, 0.001);
      last = now;
      setReading({
        sim: perf.simTick.take(),
        commit: perf.commit.take(),
        frame: perf.frame.take(),
        drawCalls: perf.drawCalls,
        bytesOut: perf.bytesOut / secs,
        bytesIn: perf.bytesIn / secs,
      });
      perf.bytesOut = 0;
      perf.bytesIn = 0;
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  if (!PERF_ENABLED) return null;

  const fps = reading?.frame ? Math.round(1000 / reading.frame.mean) : null;
  const rows: Array<[string, string]> = [
    ['sim tick ms', isHost ? stat(reading?.sim ?? null) : 'not host'],
    ['commit ms', stat(reading?.commit ?? null)],
    ['frame ms', reading?.frame ? `${stat(reading.frame)} · ${fps} fps` : '—'],
    ['draw calls', reading ? String(reading.drawCalls) : '—'],
    ['net up', reading ? rate(reading.bytesOut) : '—'],
    ['net down', reading ? rate(reading.bytesIn) : '—'],
  ];
  return (
    <div className="panel pointer-events-none absolute left-[16.25rem] top-[3.75rem] z-20 w-52 rounded-xl px-2.5 py-2 text-[11px]">
      <div className="mb-1 flex items-center justify-between">
        <span className="panel-title text-xs">Perf</span>
        <span className={`chip ${isHost ? 'chip-selected' : 'text-fg-muted'}`}>
          {isHost ? 'host' : 'peer'}
        </span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 tabular-nums">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-fg-subtle">{label}</dt>
            <dd className="text-right text-fg-muted">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
